import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";
import API_BASE from "../config";

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Check for existing session on mount
  useEffect(() => {
    const session = localStorage.getItem("user_session");
    if (session) {
      try {
        const userData = JSON.parse(session);
        if (userData && userData.id && userData.type) {
          navigate("/dashboard");
        }
      } catch (e) {
        localStorage.removeItem("user_session");
      }
    }
  }, [navigate]);

  const handleLogin = (e) => {
    e.preventDefault();
    setError("");

    if (!email.trim() || !password.trim()) {
      setError("Please enter both email and password.");
      return;
    }

    setLoading(true);

    fetch(`${API_BASE}/api/login`, {
      method: "POST",
      headers: {
        Authorization: "1",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        gmail: email,
        password: password,
        table: "users",
        token: "3232",
      }),
    })
      .then((response) => response.json())
      .then((json) => {
        setLoading(false);
        if (json.status == true) {
          const sessionData = {
            id: json.id,
            type: json.type,
            name: json.name,
            email: email,
            loggedInAt: new Date().toISOString(),
          };
          localStorage.setItem("user_session", JSON.stringify(sessionData));
          navigate("/dashboard");
        } else {
          setError("Email or password incorrect.");
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error("Login Error:", err);
        setError("An error occurred during login. Please try again.");
      });
  };

  return (
    <>
      <style>
        {`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        body {
          font-family: 'Inter', 'Segoe UI', sans-serif;
          background: #0f172a;
        }

        .login-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
          padding: 20px;
          position: relative;
          overflow: hidden;
        }

        /* Animated background shapes */
        .login-page::before {
          content: '';
          position: absolute;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%);
          top: -100px;
          right: -100px;
          border-radius: 50%;
          animation: float 8s ease-in-out infinite;
        }

        .login-page::after {
          content: '';
          position: absolute;
          width: 350px;
          height: 350px;
          background: radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%);
          bottom: -80px;
          left: -80px;
          border-radius: 50%;
          animation: float 10s ease-in-out infinite reverse;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-30px) scale(1.05); }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .login-card {
          background: rgba(30, 41, 59, 0.7);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          width: 100%;
          max-width: 420px;
          padding: 40px 36px;
          border-radius: 24px;
          position: relative;
          z-index: 1;
          animation: fadeInUp 0.6s ease-out;
        }

        .login-logo-wrap {
          display: flex;
          justify-content: center;
          margin-bottom: 8px;
        }

        .login-logo {
          width: 160px;
          filter: brightness(1.1);
        }

        .login-title {
          text-align: center;
          font-size: 22px;
          font-weight: 700;
          color: #f1f5f9;
          margin-bottom: 4px;
        }

        .login-subtitle {
          text-align: center;
          color: #94a3b8;
          font-size: 14px;
          margin-bottom: 28px;
          font-weight: 400;
        }

        .login-error {
          background: rgba(239, 68, 68, 0.12);
          color: #fca5a5;
          padding: 12px 16px;
          border-radius: 12px;
          font-size: 13px;
          margin-bottom: 20px;
          border: 1px solid rgba(239, 68, 68, 0.2);
          text-align: center;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .login-error::before {
          content: '⚠';
          font-size: 16px;
        }

        .input-group {
          margin-bottom: 20px;
        }

        .input-group label {
          display: block;
          margin-bottom: 8px;
          font-size: 13px;
          font-weight: 500;
          color: #cbd5e1;
          letter-spacing: 0.3px;
        }

        .input-wrapper {
          position: relative;
        }

        .input-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 16px;
          color: #64748b;
          pointer-events: none;
          transition: color 0.2s;
        }

        .input-wrapper input {
          width: 100%;
          padding: 14px 14px 14px 42px;
          border-radius: 12px;
          border: 1.5px solid rgba(100, 116, 139, 0.3);
          background: rgba(15, 23, 42, 0.5);
          color: #f1f5f9;
          font-size: 14px;
          font-family: 'Inter', sans-serif;
          outline: none;
          transition: all 0.25s ease;
        }

        .input-wrapper input::placeholder {
          color: #475569;
        }

        .input-wrapper input:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
          background: rgba(15, 23, 42, 0.8);
        }

        .input-wrapper input:focus + .input-icon,
        .input-wrapper input:focus ~ .input-icon {
          color: #3b82f6;
        }

        .input-wrapper input:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .password-input {
          padding-right: 48px !important;
        }

        .toggle-password {
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #64748b;
          cursor: pointer;
          font-size: 18px;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s;
          line-height: 1;
        }

        .toggle-password:hover {
          color: #94a3b8;
        }

        .login-submit {
          width: 100%;
          padding: 14px;
          border: none;
          border-radius: 12px;
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          color: #ffffff;
          font-size: 15px;
          font-weight: 600;
          font-family: 'Inter', sans-serif;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          margin-top: 4px;
          letter-spacing: 0.3px;
        }

        .login-submit:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
          transform: translateY(-1px);
          box-shadow: 0 8px 25px rgba(37, 99, 235, 0.35);
        }

        .login-submit:active:not(:disabled) {
          transform: translateY(0);
        }

        .login-submit:disabled {
          background: linear-gradient(135deg, #475569, #334155);
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .btn-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }

        .spinner {
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }

        .login-footer {
          margin-top: 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .login-footer span {
          font-size: 13px;
          color: #64748b;
          cursor: pointer;
          transition: color 0.2s;
          font-weight: 500;
        }

        .login-footer span:hover {
          color: #3b82f6;
        }

        .divider {
          display: flex;
          align-items: center;
          margin: 24px 0 0 0;
          gap: 12px;
        }

        .divider::before,
        .divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: rgba(100, 116, 139, 0.2);
        }

        .divider span {
          font-size: 12px;
          color: #475569;
          font-weight: 500;
        }

        /* ===== Responsive ===== */
        @media (max-width: 480px) {
          .login-card {
            padding: 32px 24px;
            border-radius: 20px;
            margin: 10px;
          }

          .login-logo {
            width: 140px;
          }

          .login-title {
            font-size: 20px;
          }

          .login-subtitle {
            font-size: 13px;
          }

          .input-wrapper input {
            padding: 12px 12px 12px 40px;
            font-size: 14px;
          }

          .password-input {
            padding-right: 44px !important;
          }

          .login-submit {
            padding: 13px;
            font-size: 14px;
          }
        }

        @media (max-width: 360px) {
          .login-card {
            padding: 28px 20px;
          }

          .login-footer {
            flex-direction: column;
            gap: 10px;
          }
        }

        @media (min-width: 1200px) {
          .login-card {
            max-width: 440px;
            padding: 48px 44px;
          }
        }
        `}
      </style>

      <div className="login-page">
        <form className="login-card" onSubmit={handleLogin}>
          <div className="login-logo-wrap">
            <img src={logo} alt="SBRE Logo" className="login-logo" />
          </div>
          <h1 className="login-title">Welcome Back</h1>
          <p className="login-subtitle">Sign in to continue to your dashboard</p>

          {error && <div className="login-error">{error}</div>}

          <div className="input-group">
            <label htmlFor="login-email">Email Address</label>
            <div className="input-wrapper">
              <input
                id="login-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                required
              />
              <span className="input-icon">✉</span>
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="login-password">Password</label>
            <div className="input-wrapper">
              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                className="password-input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                required
              />
              <span className="input-icon">🔒</span>
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "🙈" : "👁"}
              </button>
            </div>
          </div>

          <button type="submit" className="login-submit" disabled={loading}>
            <span className="btn-content">
              {loading && <span className="spinner"></span>}
              {loading ? "Signing in..." : "Sign In"}
            </span>
          </button>

          <div className="login-footer">
            <span>Forgot Password?</span>
            <span>Create Account</span>
          </div>

          <div className="divider">
            <span>SBRE Securities</span>
          </div>
        </form>
      </div>
    </>
  );
};

export default Login;
